/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import Grid from "@mui/material/Grid";
import { useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { Box, Button, TextField, MenuItem, Checkbox, Typography } from "@mui/material";

import { RootState } from "../../../../../store";
import Panel from "../../../../../components/Panel";
import { tl } from "../../../../../components/translate";
import { ILeaveType } from "../../../../../interfaces/LeaveInterface";
import { usePostLeaveType, usePatchLeaveType } from "../../../../../hooks/query/useLeaveTypes";
import { LeaveAccrualFrequency, LeaveAccrualMethod } from "../../../../../constants/leave";

interface LeaveTypeCreateEditProps {
  onClose: () => void;
  leave?: Partial<ILeaveType> | null;
  onSuccess: (data?: ILeaveType) => void;
}

// eslint-disable-next-line import/prefer-default-export
export function LeaveTypeCreateEdit({
  onClose,
  leave,
  onSuccess
}: LeaveTypeCreateEditProps): React.ReactNode {
  const isEdit = !!leave?.id;

  const rcId = useSelector((state: RootState) => state.userContext.resourceCentre?.id);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    setValue
  } = useForm<ILeaveType>({
    defaultValues: {
      name: leave?.name || "",
      code: leave?.code || "",
      description: leave?.description || "",
      quota: leave?.quota ?? 0,
      accrualFrequency: leave?.accrualFrequency || LeaveAccrualFrequency.YEARLY,
      accrualMethod: leave?.accrualMethod || LeaveAccrualMethod.ACCRUAL,
      isPaid: leave?.isPaid ?? true,
      isProrated: leave?.isProrated ?? true,
      isMappable: leave?.isMappable ?? false,
      isEncashable: leave?.isEncashable ?? false,
      isCarryable: leave?.isCarryable ?? false,
      maxCarryableDays: leave?.maxCarryableDays ?? undefined,
      resourceCentreId: rcId || 0
    }
  });

  const postMutation = usePostLeaveType();
  const patchMutation = usePatchLeaveType();

  const isCarryable = watch("isCarryable");

  const onSubmit = async (data: ILeaveType) => {
    if (!rcId) return;
    const payload: Partial<ILeaveType> = {
      ...data,
      resourceCentreId: rcId
    };

    if (!payload?.isCarryable) {
      payload.maxCarryableDays = 0;
    }

    if (isEdit && leave?.id) {
      patchMutation.mutate(
        { id: leave.id, data: payload },
        {
          onSuccess: (response) => {
            if (response.success) {
              onSuccess(response.data);
              onClose();
            }
          }
        }
      );
    } else {
      postMutation.mutate(payload, {
        onSuccess: (response) => {
          if (response.success) {
            onSuccess(response.data);
            onClose();
          }
        }
      });
    }
  };

  return (
    <Panel
      title={isEdit ? "Edit Leave" : "Add Leave"}
      onClose={onClose}
      footer={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "flex-end",
            gap: 2
          }}
        >
          <Button onClick={onClose}>{tl("cancel")}</Button>
          <Button
            type="submit"
            variant="contained"
            form="leave-form"
            disabled={isSubmitting || postMutation.isLoading || patchMutation.isLoading}
          >
            {isEdit ? tl("update") : tl("create")}
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <form id="leave-form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            {/* Name */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="name"
                control={control}
                rules={{ required: "Name is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Leave Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>

            {/* Code */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="code"
                control={control}
                rules={{ required: "Code is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Code"
                    error={!!errors.code}
                    helperText={errors.code?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>

            {/* Quota */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="quota"
                control={control}
                rules={{
                  required: "Quota is required",
                  min: { value: 0, message: "Quota must be >= 0" }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="outlined"
                    label="Quota (Days)"
                    type="number"
                    error={!!errors.quota}
                    helperText={errors.quota?.message}
                    onFocus={(event) => event.target.select()}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>

            {/* Accrual Period */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="accrualFrequency"
                control={control}
                rules={{ required: "Accrual period is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    variant="outlined"
                    label="Accrual Period"
                    error={!!errors.accrualFrequency}
                    helperText={errors.accrualFrequency?.message}
                    slotProps={{ inputLabel: { shrink: true } }}
                  >
                    <MenuItem value={LeaveAccrualFrequency.MONTHLY}>Monthly</MenuItem>
                    <MenuItem value={LeaveAccrualFrequency.YEARLY}>Yearly</MenuItem>
                  </TextField>
                )}
              />
            </Grid>

            {/* Accrual Type */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                name="accrualMethod"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    variant="outlined"
                    label="Accrual Type"
                    slotProps={{ inputLabel: { shrink: true } }}
                  >
                    <MenuItem value={LeaveAccrualMethod.LUMP_SUM}>Lump Sum</MenuItem>
                    <MenuItem value={LeaveAccrualMethod.ACCRUAL}>Accrual</MenuItem>
                  </TextField>
                )}
              />
            </Grid>

            {/* Max Carryable Days - shown only when isCarryable is true */}
            {isCarryable && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="maxCarryableDays"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      label="Max Carryable Days"
                      type="number"
                      onFocus={(event) => event.target.select()}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  )}
                />
              </Grid>
            )}

            {/* Description */}
            <Grid size={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    label="Description"
                    onFocus={(event) => event.target.select()}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            </Grid>

            {/* Leave Options */}
            <Grid size={12}>
              <Typography variant="body2" fontWeight={600} mb={1.5}>
                Leave Options
              </Typography>
              <Box display="flex" flexDirection="column" gap={0.5}>
                {(
                  [
                    {
                      name: "isPaid",
                      label: "Paid Leave",
                      description:
                        "Employees receive their regular salary during this leave. Uncheck for unpaid leave types."
                    },
                    {
                      name: "isProrated",
                      label: "Prorated",
                      description:
                        "Leave quota is calculated proportionally based on the employee's joining date within the year, rather than granting the full annual quota upfront."
                    },
                    {
                      name: "isMappable",
                      label: "Mappable",
                      description:
                        "This leave can be linked to government or statutory leave categories (e.g. public holidays, maternity leave)."
                    },
                    {
                      name: "isEncashable",
                      label: "Encashable",
                      description:
                        "Unused leave balance can be converted to a cash payout at the end of a period or upon resignation."
                    },
                    {
                      name: "isCarryable",
                      label: "Carryable",
                      description:
                        "Unused leave days can be carried forward to the next period. Set 'Max Carryable Days' above to cap the amount."
                    }
                  ] as const
                ).map(({ name, label, description }) => (
                  <Controller
                    key={name}
                    name={name}
                    control={control}
                    render={({ field }) => (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          py: 1
                        }}
                      >
                        <Checkbox
                          checked={!!field.value}
                          onChange={(e) => {
                            const { checked } = e.target;
                            field.onChange(checked);

                            if (name === "isCarryable" && !checked) {
                              setValue("maxCarryableDays", 0);
                            }
                          }}
                          size="small"
                          sx={{ p: 0, flexShrink: 0 }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {description}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Panel>
  );
}
